import { useEffect, useRef, useState, useCallback } from 'react';
import * as pbi from 'powerbi-client';
import {
  getReportEmbed, getPaginatedEmbed, getDashboardEmbed,
  getVisualSettings, saveVisualSettings
} from '../api/client.js';

const service = new pbi.service.Service(
  pbi.factories.hpmFactory,
  pbi.factories.wpmpFactory,
  pbi.factories.routerFactory
);

const FETCHERS = {
  report: getReportEmbed,
  paginated: getPaginatedEmbed,
  dashboard: getDashboardEmbed
};

/**
 * Embeds a Power BI report and manages its lifecycle.
 *
 * Improvements over the old dashClient.js:
 *  - the container is reset and the embed released on unmount, instead of
 *    leaving orphaned iframes behind on every nav click
 *  - embed tokens expire in ~an hour; this refreshes before expiry rather
 *    than letting the report silently go stale
 *  - saved slicer state is applied after the report loads, and restore
 *    failures do not take the whole view down
 */
export function usePowerBiEmbed({ kind, id, containerRef, onLoaded }) {
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const embedRef = useRef(null);
  const refreshTimer = useRef(null);

  const applySavedSlicers = useCallback(async (report, reportId) => {
    try {
      const saved = await getVisualSettings(reportId);
      if (!saved.length) return;

      const pages = await report.getPages();
      for (const state of saved) {
        const page = pages.find(p => p.name === state.pageId);
        if (!page) continue;
        const visuals = await page.getVisuals();
        const slicer = visuals.find(v => v.name === state.slicerId && v.type === 'slicer');
        if (!slicer || !state.table || !state.column) continue;

        await slicer.setSlicerState({
          filters: [{
            $schema: 'http://powerbi.com/product/schema#basic',
            target: { table: state.table, column: state.column },
            operator: 'In',
            values: Array.isArray(state.value) ? state.value : [state.value]
          }]
        });
      }
    } catch (err) {
      // A slicer that no longer exists must not break the report.
      console.warn('Could not restore all slicer state', err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    setStatus('loading');
    setError(null);

    (async () => {
      try {
        const config = await FETCHERS[kind](id);
        if (cancelled) return;

        service.reset(container);

        const isMobilePortrait =
          window.innerWidth < 768 && window.innerHeight > window.innerWidth;

        /* A paginated report is an RDL, and it does not take a report's
           configuration.
           -------------------------------------------------------------------
           `type` is 'report' for both - that part was right. What is not
           interchangeable is everything else. filterPaneEnabled,
           navContentPaneEnabled and layoutType are report-only; RDL has none of
           those surfaces. `permissions` is likewise meaningless for something
           that cannot be edited.

           Sent anyway, the SDK accepted the configuration and then signalled
           nothing at all - no `loaded`, no `error`, an empty console and a
           spinner that never resolved. A rejection would have been kinder.

           Note the nesting: parameterPanel sits under `commands`. v1 sends it
           flat, and that is not a difference to copy - v1 runs SDK 2.22.3 and
           this is 2.24.1, where IPaginatedReportSettings puts the panel under
           commands. Microsoft's example:

             settings: { commands: { parameterPanel: { enabled, expanded } } }

           https://learn.microsoft.com/javascript/api/overview/powerbi/embed-paginated-report */
        const paginated = kind === 'paginated';

        const embed = service.embed(container, {
          type: kind === 'dashboard' ? 'dashboard' : 'report',
          id: config.reportId ?? config.dashboardId,
          embedUrl: config.embedUrl,
          accessToken: config.token,
          tokenType: pbi.models.TokenType.Embed,
          ...(paginated ? {} : { permissions: pbi.models.Permissions.Read }),
          settings: paginated
            ? { commands: { parameterPanel: { enabled: true, expanded: true } } }
            : {
              filterPaneEnabled: false,
              navContentPaneEnabled: kind === 'report',
              layoutType: isMobilePortrait ? pbi.models.LayoutType.MobilePortrait : undefined
            }
        });

        embedRef.current = embed;

        embed.off('loaded');
        embed.on('loaded', async () => {
          if (cancelled) return;
          setStatus('ready');
          if (kind === 'report') await applySavedSlicers(embed, config.reportId);
          onLoaded?.(config);
        });

        embed.off('error');
        embed.on('error', (event) => {
          console.error('Power BI embed error', event.detail);
          if (!cancelled) { setError(event.detail); setStatus('error'); }
        });

        // Refresh the token a minute before it expires.
        if (config.expiresUtc) {
          const ms = new Date(config.expiresUtc).getTime() - Date.now() - 60_000;
          if (ms > 0) {
            refreshTimer.current = setTimeout(async () => {
              try {
                const fresh = await FETCHERS[kind](id);
                await embedRef.current?.setAccessToken(fresh.token);
              } catch (err) {
                console.warn('Embed token refresh failed', err);
              }
            }, ms);
          }
        }
      } catch (err) {
        if (!cancelled) { setError(err); setStatus('error'); }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(refreshTimer.current);
      if (container) service.reset(container);
      embedRef.current = null;
    };
  }, [kind, id, containerRef, applySavedSlicers, onLoaded]);

  /** Persist the current slicer selections for this report. */
  const saveCurrentSlicers = useCallback(async () => {
    const report = embedRef.current;
    if (!report || kind !== 'report') return;

    const states = [];
    const pages = await report.getPages();
    for (const page of pages) {
      const visuals = await page.getVisuals();
      for (const visual of visuals.filter(v => v.type === 'slicer')) {
        const state = await visual.getSlicerState();
        const filter = state.filters?.[0];
        if (!filter?.target) continue;
        states.push({
          pageId: page.name,
          slicerId: visual.name,
          table: filter.target.table,
          column: filter.target.column,
          value: filter.values ?? []
        });
      }
    }
    await saveVisualSettings(id, states);
    return states.length;
  }, [id, kind]);

  const reload = useCallback(() => embedRef.current?.reload(), []);
  const fullscreen = useCallback(() => embedRef.current?.fullscreen(), []);
  const print = useCallback(() => embedRef.current?.print(), []);

  return { status, error, saveCurrentSlicers, reload, fullscreen, print };
}
